// src/components/BuyScreen.tsx
import init, { Session, UUID, UserCommandResponseVec } from "keyserver-client";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { fetchPublic, fetchKey, KEYSERVER_DOMAIN, localStorageKeyName } from "../Client";
import {importRootKey} from "../Crypto";
import {getMerged} from "../Metadata"
import { StoredMetadata } from "../Metadata";
import { JsonRpcProvider, BrowserProvider } from "ethers";
import { SepoliaChain, SepoliaChainReadOnly } from "../Chains";

interface PublicData {
  metadata?: {
    name?: string;
    description?: string;
    image?: string;
    content?: string;
    confidential?: {
      key_id?: string;
      kdf: number;
      data?: any;
    };
  };
  owner: string;
}
  
const BuyScreen: React.FC = () => {
  // Extract route parameters.
  const { chain, collection, tokenId } = useParams<{
    chain: string;
    collection: string;
    tokenId: string;
  }>();
  
  const [nftData, setNftData] = useState<PublicData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paid, setPaid] = useState<boolean>(false);
  const [paymentProcessing, setPaymentProcessing] = useState<boolean>(false);
  const [decryptedMetadata, setDecryptedMetadata] = useState<any>(null);
  
  // Construct the buy URL once.
  const buyUrl = `${window.location.origin}/${chain}/${collection}/${tokenId}`;
  
  // Fetch NFT public data (using a read-only chain) on page load.
  useEffect(() => {
    async function loadNftData() {
      try {
        const tokenIdBigInt = BigInt(tokenId!);
        // Create a read-only provider (Infura endpoint for Sepolia).
        const browserProvider = new BrowserProvider(window.ethereum as any);
        // Use the read-only chain to fetch public data.
        const chainInstance = new SepoliaChainReadOnly(browserProvider);
        const data = await fetchPublic(collection!, tokenIdBigInt, chainInstance);
        setNftData(data);
      } catch (err) {
        console.error("Failed to fetch NFT data:", err);
        setError("Failed to fetch NFT data.");
      } finally {
        setLoading(false);
      }
    }
    loadNftData();
  }, [collection, tokenId, chain]);
  
  // Simulate payment.
  const handlePay = async () => {
    setPaymentProcessing(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setPaid(true);
    } catch (err) {
      console.error(err);
      setError("Payment failed, please try again.");
    } finally {
      setPaymentProcessing(false);
    }
  };
  
  // Decrypt metadata after payment using a chain instance with a signer.
  const handleDecrypt = async () => {
    try {
      if (!nftData || !nftData.metadata) {
        throw new Error("NFT metadata is missing.");
      }
      const confidential = nftData.metadata.confidential;
      if (!confidential || !confidential.key_id) {
        throw new Error("No key_id found in NFT metadata.");
      }
      // We expect the encrypted blob to be in the 'data' property.
      if (confidential.data === undefined) {
        throw new Error("Encrypted metadata not found in confidential field.");
      }
  
      // Patch the confidential object into the expected shape:
      // { key_id: string; kdf: number; data: string }
      type PatchedConfidential = {
        key_id: string;
        kdf: number;
        data: string;
      };
      const patchedConfidential: PatchedConfidential = {
        key_id: confidential.key_id,
        kdf: Number(confidential.kdf),
        data: String(confidential.data),
      };
  
      // Initialize the WASM module.
      await init();

      // Connect to MetaMask using BrowserProvider.
      if (!window.ethereum) {
        throw new Error("MetaMask is not installed.");
      }
      const provider = new BrowserProvider(window.ethereum as any);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      const chainInstance = new SepoliaChain(signer);
  
      // Fetch the raw decryption key from the keyserver.
      let rawKey;
      try {
        rawKey = await fetchKey(BigInt(tokenId!) as bigint, chainInstance, confidential.key_id);
      } catch (e: any) {
        console.error("fetchKey error:", e);
         //Check if the error message indicates unauthorized access.
        if (typeof e === "string" && e.includes("ErrUnauthorized") ||
            (e && e.message && e.message.includes("Unauthorized"))) {
          throw new Error("Unauthorized: Please sign the authentication message in your NFT wallet.");
        }
        throw e;
      }
      
      const rawKeyBytes = new Uint8Array(rawKey.buffer);
      console.log("Raw key bytes:", rawKeyBytes);
      //const importedKey = await importRootKey(rawKeyBytes);
      
      const localStorageKey = localStorageKeyName(collection!, BigInt(tokenId!));
      const keyB64 = localStorage.getItem(localStorageKey);
      //const keyB64 = localStorage.getItem("cnft-decryption-key-sepolia/0x5251378f7036536325b9e2dab6d93c5924108d67/61");
      const key = await importRootKey(Uint8Array.fromBase64(keyB64!));
      
      
      // Remove any extraneous confidential properties (if present).
      const publicMetadata = { ...nftData.metadata };
      // Build a complete metadata object by merging public metadata with our patched confidential field.
      const metadataCnft: StoredMetadata = {
        ...publicMetadata,
        confidential: patchedConfidential,
      };
      
      const mergedMetadata = await getMerged(metadataCnft, key, false, true)
      //console.log("Metadatacnft, importedkey:",metadataCnft,importedKey)
  
      // Decrypt (merge) the metadata.
      //const mergedMetadata = await getMerged(metadataCnft, importedKey, false, true);
      setDecryptedMetadata(mergedMetadata);
    } catch (err: any) {
      console.error("Decryption failed:", err);
      setError("Decryption failed: " + err.message);
    }
  };

  
  
  if (loading) return <div>Loading NFT information...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!nftData) return <div>No NFT data found.</div>;
  
  return (
    <div className="card">
      <h2>Buy NFT</h2>
      <p>
        <strong>Buy URL:</strong> {buyUrl}
      </p>
      {nftData.metadata && nftData.metadata.name && <h3>{nftData.metadata.name}</h3>}
      {nftData.metadata && nftData.metadata.description && <p>{nftData.metadata.description}</p>}
      {nftData.metadata && nftData.metadata.image && (
        <img src={nftData.metadata.image} alt="NFT Thumbnail" style={{ maxWidth: "300px" }} />
      )}
      {!paid ? (
        <>
          <button onClick={handlePay} disabled={paymentProcessing}>
            {paymentProcessing ? "Processing Payment..." : "Pay Now"}
          </button>
          <p>Price: [Display price here]</p>
        </>
      ) : (
        <div>
          <h3>Payment Successful!</h3>
          <button onClick={handleDecrypt}>Decrypt Metadata</button>
          {decryptedMetadata ? (
            <div>
             <h4>Decrypted Metadata:</h4>
              <pre>{JSON.stringify(decryptedMetadata, null, 2)}</pre>
              {/* If the decrypted metadata contains an image$confidential property, display it */}
              {decryptedMetadata["image$confidential"] &&
                typeof decryptedMetadata["image$confidential"] === "string" && (
                  <div>
                    <h4>Confidential Image:</h4>
                    <img
                      src={decryptedMetadata["image$confidential"]}
                      alt="Confidential NFT"
                      style={{ maxWidth: "600px" }}
                    />
                  </div>
              )}
            </div>
          ) : (
            <p>Click "Decrypt Metadata" to reveal NFT details.</p>
          )}
        </div>
      )}
    </div>
  );
};
  
export default BuyScreen;